import { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import type { Lang } from "@/i18n";
import { useI18n } from "@/i18n";
import { openLink, hapticTap } from "@/tg/init";
import Logo from "@/components/Logo";
import { LEVELS, MERCH, pickLoc, cumulativeAdds } from "@/data/membership";
import ImageWithFallback from "@/components/ImageWithFallback";
import { getMe, peekMe, type Membership } from "@/api";
import { SUBSCRIBE_URL, DONATE_URL } from "@/lib/links";

export default function LevelsMerch() {
  const { lang, setLang, t } = useI18n();
  const [me, setMe] = useState<Membership | null>(() => peekMe(lang) ?? null);

  useEffect(() => {
    getMe(lang).then(setMe).catch(() => setMe(null));
  }, [lang]);

  function handleLangToggle() {
    hapticTap();
    setLang((lang === "uk" ? "en" : "uk") as Lang);
  }

  return (
    <div style={{ background: "var(--tg-bg)", minHeight: "100%" }}>
      {/* Sticky header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          height: 56,
          background: "#ffffff",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <Logo size={34} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={handleLangToggle}
            aria-label="Switch language"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--tint)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {lang === "uk" ? "UA" : "EN"}
          </button>
          <button
            onClick={() => { hapticTap(); openLink(DONATE_URL); }}
            aria-label="Donate"
            style={{
              padding: "0 16px",
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "var(--r-pill)",
              background: "var(--brand-blue)",
              color: "#ffffff",
              border: "none",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {t("donate")}
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "16px 16px 32px" }}>
        {/* Впритул logo hero */}
        <img
          src="/tiers/vprytul-logo.png"
          alt="ВПРИТУЛ"
          style={{ display: "block", width: 132, height: 132, objectFit: "cover", borderRadius: "var(--r-card)", margin: "0 auto 24px" }}
        />

        {/* Levels section */}
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--tg-hint)",
            margin: "0 0 12px",
          }}
        >
          {t("levels")}
        </p>
        {LEVELS.map((lvl) => (
          <LevelCard key={lvl.level} lvl={lvl} me={me} lang={lang} t={t} />
        ))}

        {/* Merch section */}
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--tg-hint)",
            margin: "24px 0 12px",
          }}
        >
          {t("merch")}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {MERCH.map((m) => (
            <div
              key={m.id}
              style={{
                background: "var(--tint)",
                borderRadius: "var(--r-card)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  overflow: "hidden",
                  borderRadius: 8,
                }}
              >
                <ImageWithFallback
                  src={null}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div style={{ padding: "8px 10px 10px" }}>
                <p
                  style={{
                    margin: "0 0 4px",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: '"e-UkraineHead", system-ui, sans-serif',
                    color: "var(--ink)",
                  }}
                >
                  {pickLoc(m.name, lang)}
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--ink)",
                      background: "var(--border)",
                      borderRadius: "var(--r-pill)",
                      padding: "2px 8px",
                    }}
                  >
                    {lang === "uk" ? `Рівень ${m.unlockLevel}` : `Level ${m.unlockLevel}`}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--muted)" }}>{t("coming_soon")}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LevelCard — owns its own open/closed state
// ---------------------------------------------------------------------------

type TFn = ReturnType<typeof useI18n>["t"];

interface LevelCardProps {
  lvl: (typeof LEVELS)[number];
  me: Membership | null;
  lang: string;
  t: TFn;
}

function LevelCard({ lvl, me, lang, t }: LevelCardProps) {
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();

  const isCurrent = me !== null && lvl.level === me.level;
  const isUpgrade = me !== null && lvl.level > me.level;

  const bullets = cumulativeAdds(lvl.level, lang);

  return (
    <div
      style={{
        position: "relative",
        background: "var(--tint)",
        borderRadius: "var(--r-card)",
        padding: 16,
        marginBottom: 12,
        border: isCurrent ? "1.5px solid var(--brand-blue)" : "none",
      }}
    >
      {isCurrent && (
        <span
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            fontSize: 11,
            fontWeight: 600,
            color: "var(--brand-blue)",
            background: "#fff",
            border: "1px solid var(--border)",
            borderRadius: "var(--r-pill)",
            padding: "4px 10px",
          }}
        >
          {lang === "uk" ? "Ваш рівень" : "Your level"}
        </span>
      )}

      {/* Cover banner image */}
      <img
        src={lvl.cover}
        alt=""
        loading="lazy"
        style={{ width: "100%", aspectRatio: "23 / 10", objectFit: "cover", borderRadius: 8, display: "block", marginBottom: 12 }}
      />

      {/* Name + price row — plain, non-interactive */}
      <div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "var(--muted)",
            letterSpacing: "0.04em",
          }}
        >
          {lvl.name}
        </span>
        <div
          style={{
            fontFamily: '"e-UkraineHead", system-ui, sans-serif',
            fontSize: 24,
            fontWeight: 600,
            color: "var(--ink)",
            lineHeight: 1.1,
            margin: "2px 0 0",
          }}
        >
          {lvl.threshold} ₴
          <span style={{ fontSize: 14, fontWeight: 400, color: "var(--muted)" }}>
            {lang === "uk" ? "/міс" : "/mo"}
          </span>
        </div>
      </div>

      {/* Benefit bullets — always visible */}
      <div style={{ marginTop: 10 }}>
        {bullets.map((r, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              color: "var(--text)",
              lineHeight: 1.5,
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "var(--brand-blue)",
                flexShrink: 0,
              }}
            />
            {r}
          </div>
        ))}
      </div>

      {/* Details toggle */}
      <button
        onClick={() => { hapticTap(); setOpen((o) => !o); }}
        aria-expanded={open}
        aria-controls={`level-body-${lvl.level}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          padding: 0,
          marginTop: 12,
          cursor: "pointer",
          color: "var(--brand-blue)",
          fontSize: 14,
          fontWeight: 500,
          WebkitTapHighlightColor: "transparent",
        }}
      >
        {lang === "uk" ? "Деталі" : "Details"}
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.2 }}
          style={{ display: "inline-flex" }}
        >
          <ChevronDown size={18} color="var(--brand-blue)" />
        </motion.span>
      </button>

      {/* Collapsible description */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`level-body-${lvl.level}`}
            key="body"
            initial={reducedMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            {...(!reducedMotion && { exit: { height: 0, opacity: 0 } })}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.2, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <p style={{ fontSize: 14, lineHeight: 1.5, color: "var(--muted)", margin: 0, paddingTop: 12 }}>
              {pickLoc(lvl.description, lang)}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade CTA — always visible */}
      {isUpgrade && (
        <button
          onClick={() => { hapticTap(); openLink(SUBSCRIBE_URL); }}
          style={{
            marginTop: 12,
            width: "100%",
            height: 44,
            borderRadius: "var(--r-pill)",
            background: "var(--brand-blue)",
            color: "#fff",
            border: "none",
            fontFamily: '"e-Ukraine", system-ui, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {lang === "uk"
            ? `Підвищити до рівня ${lvl.level}`
            : `Upgrade to Level ${lvl.level}`}
        </button>
      )}
    </div>
  );
}
